/**
 * src/pages/Inbox.jsx — the studio's shared email inbox.
 *
 * Clients email <slug>@studio.tattoo-lab.co.uk (shown in Settings → Branding)
 * and it lands here; replies go out from the same address. Any artist in the
 * studio can read and reply — it works like a front desk, not a personal
 * mailbox, so opening a thread marks it read for everyone.
 *
 * Layout: thread list on the left, the open thread on the right (/inbox/:id).
 * Below the 760px breakpoint only one pane shows at a time.
 *
 * Views:
 *  - Inbox: conversations — anything a client wrote, or a studio member did.
 *  - Automated: booking confirmations, consent links and aftercare, which are
 *    logged so a client's reply threads under the email it answers. A thread
 *    moves to Inbox by itself the moment the client replies.
 *  - Archived.
 *
 * Email HTML is untrusted. Bodies show as plain text by default; "Show
 * formatting" renders the HTML in an iframe with an empty `sandbox` (no
 * scripts, no forms, no same-origin access) and a CSP that allows only inline
 * styles and data: images. Remote images stay blocked on purpose — they are
 * how senders track opens — and a srcdoc frame inherits the app's own CSP
 * (public/_headers) anyway, which would block most of them regardless.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, apiUrl } from '../lib/api.js';
import { useApi } from '../lib/useApi.js';
import { formatDateTime } from '../lib/format.js';
import { Empty, ErrorBanner, Loader, PageHeader } from '../components/ui.jsx';
import { Dialog } from '../components/ui/index.js';

const VIEWS = [
  { value: 'inbox', label: 'Inbox' },
  { value: 'automated', label: 'Automated' },
  { value: 'archived', label: 'Archived' },
];

/** Prepended to every rendered email: nothing loads but inline CSS and data: images. */
const EMAIL_CSP =
  "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; style-src 'unsafe-inline'; img-src data:\">";

export default function Inbox() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState('inbox');
  const [search, setSearch] = useState('');
  const [composing, setComposing] = useState(false);

  const query = `/api/inbox/threads?view=${view}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
  const list = useApi(query, [view, search]);
  const threads = list.data?.threads ?? [];

  return (
    <>
      <PageHeader
        title="Inbox"
        subtitle="Email with your clients, from your studio's own address."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setComposing(true)}>
            New email
          </button>
        }
      />

      <div className={`inbox${threadId ? ' inbox-has-thread' : ''}`}>
        <section className="card inbox-list" aria-label="Conversations">
          <div className="row wrap" style={{ marginBottom: 12 }}>
            {VIEWS.map((v) => (
              <button
                key={v.value}
                type="button"
                className={`btn btn-sm${view === v.value ? ' btn-primary' : ''}`}
                aria-pressed={view === v.value}
                onClick={() => setView(v.value)}
              >
                {v.label}
              </button>
            ))}
          </div>
          <label htmlFor="inbox-search" className="ui-visually-hidden">
            Search conversations
          </label>
          <input
            id="inbox-search"
            type="search"
            placeholder="Search by name, email or subject"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <Loader loading={list.loading && !list.data} error={list.error}>
            {threads.length === 0 ? (
              <Empty>
                {view === 'inbox'
                  ? 'No conversations yet. When a client emails your studio address, it appears here.'
                  : 'Nothing here.'}
              </Empty>
            ) : (
              <ul className="inbox-threads">
                {threads.map((t) => (
                  <li key={t.id}>
                    <Link
                      to={`/inbox/${t.id}`}
                      className={`inbox-thread${t.id === threadId ? ' active' : ''}${t.unreadCount ? ' unread' : ''}`}
                      aria-current={t.id === threadId ? 'page' : undefined}
                    >
                      <div className="row-between">
                        <span className="inbox-thread-name">
                          {t.clientName ?? t.counterpartName ?? t.counterpartEmail}
                        </span>
                        <span className="muted small">{formatDateTime(t.lastMessageAt)}</span>
                      </div>
                      <div className="inbox-thread-subject">
                        {t.unreadCount ? (
                          <span className="inbox-dot" aria-label={`${t.unreadCount} unread`} />
                        ) : null}
                        {t.subject}
                      </div>
                      <div className="muted small inbox-thread-snippet">{t.snippet}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Loader>
        </section>

        <section className="card inbox-thread-pane" aria-label="Conversation">
          {threadId ? (
            <Thread
              key={threadId}
              threadId={threadId}
              onBack={() => navigate('/inbox')}
              onChanged={list.reload}
            />
          ) : (
            <Empty>Select a conversation to read it.</Empty>
          )}
        </section>
      </div>

      {composing ? (
        <ComposeDialog
          onClose={() => setComposing(false)}
          onSent={(id) => {
            setComposing(false);
            list.reload();
            navigate(`/inbox/${id}`);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * One open conversation, with the reply box underneath.
 *
 * @param {{ threadId: string, onBack: () => void, onChanged: () => void }} props
 * @returns {JSX.Element}
 */
function Thread({ threadId, onBack, onChanged }) {
  const { data, error, loading, reload } = useApi(`/api/inbox/threads/${threadId}`, [threadId]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Opening the thread marked it read on the server; refresh the list (and
  // the sidebar badge, which polls on its own) so the dot disappears.
  useEffect(() => {
    if (data) {
      onChanged();
      window.dispatchEvent(new Event('inbox:changed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.thread?.id]);

  if (loading && !data) return <div className="empty loading-pulse">Loading…</div>;
  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  const { thread, messages } = data;

  const toggleArchive = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await api.patch(`/api/inbox/threads/${thread.id}`, { archived: !thread.archived });
      onChanged();
      onBack();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <div className="row-between wrap">
        <div>
          <button type="button" className="btn btn-sm inbox-back" onClick={onBack}>
            ← All conversations
          </button>
          <h2 style={{ margin: 0 }}>{thread.subject}</h2>
          <div className="muted small">
            {thread.clientId ? (
              <Link to={`/clients/${thread.clientId}`}>{thread.clientName ?? thread.counterpartEmail}</Link>
            ) : (
              thread.counterpartName ?? thread.counterpartEmail
            )}
            {' · '}
            {thread.counterpartEmail}
          </div>
        </div>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={toggleArchive}>
          {thread.archived ? 'Move to inbox' : 'Archive'}
        </button>
      </div>
      <ErrorBanner>{actionError}</ErrorBanner>

      <ol className="inbox-messages">
        {messages.map((m) => (
          <Message key={m.id} message={m} />
        ))}
      </ol>

      <ReplyBox threadId={thread.id} onSent={reload} />
    </div>
  );
}

/**
 * A single email in a thread.
 *
 * @param {{ message: object }} props
 * @returns {JSX.Element}
 */
function Message({ message: m }) {
  const [formatted, setFormatted] = useState(false);
  const outbound = m.direction === 'outbound';
  const who = outbound
    ? m.kind === 'automated'
      ? 'Sent automatically'
      : `${m.sentByName ?? m.fromName ?? 'Studio'}`
    : m.fromName ?? m.fromEmail;

  return (
    <li className={`inbox-message${outbound ? ' outbound' : ''}`}>
      <div className="row-between wrap">
        <strong>{who}</strong>
        <span className="muted small">
          {formatDateTime(m.createdAt)}
          {m.status === 'failed' ? <span className="inbox-failed"> · Not delivered</span> : null}
        </span>
      </div>
      {formatted && m.html ? (
        <iframe
          className="inbox-html"
          title={`Email: ${m.subject}`}
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={`${EMAIL_CSP}${m.html}`}
        />
      ) : (
        <div className="inbox-body">{m.text}</div>
      )}
      {m.attachments.length ? (
        <ul className="row wrap inbox-attachments">
          {m.attachments.map((a) => (
            <li key={a.id}>
              <a className="btn btn-sm" href={apiUrl(`/api/inbox/attachments/${a.id}`)} download>
                {a.filename} <span className="muted">({formatSize(a.size)})</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {m.html ? (
        <button type="button" className="btn btn-sm" onClick={() => setFormatted((f) => !f)}>
          {formatted ? 'Show plain text' : 'Show formatting'}
        </button>
      ) : null}
    </li>
  );
}

/**
 * The reply box. Keeps the draft when a send fails.
 *
 * @param {{ threadId: string, onSent: () => void }} props
 * @returns {JSX.Element}
 */
function ReplyBox({ threadId, onSent }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/inbox/threads/${threadId}/reply`, { body });
      setBody('');
      onSent();
    } catch (err) {
      setError(err.message);
      // A 502 means it was recorded as "Not delivered" — show that too.
      onSent();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={submit}>
      <ErrorBanner>{error}</ErrorBanner>
      <label htmlFor="inbox-reply">Reply</label>
      <textarea
        id="inbox-reply"
        rows={5}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your reply…"
        required
        aria-describedby="inbox-reply-hint"
      />
      {/* Same appended sign-off as a new email — see ComposeDialog. */}
      <p id="inbox-reply-hint" className="muted small">
        No need to sign off — your name and studio name are added to the end of the email
        automatically.
      </p>
      <div>
        <button type="submit" className="btn btn-primary" disabled={busy || !body.trim()}>
          {busy ? 'Sending…' : 'Send reply'}
        </button>
      </div>
    </form>
  );
}

/**
 * New-email form.
 *
 * @param {{ onClose: () => void, onSent: (threadId: string) => void }} props
 * @returns {JSX.Element}
 */
function ComposeDialog({ onClose, onSent }) {
  const [form, setForm] = useState({ to: '', subject: '', body: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/inbox/threads', form);
      onSent(res.threadId);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title="New email" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>
        <div>
          <label htmlFor="compose-to">To</label>
          <input id="compose-to" type="email" value={form.to} onChange={set('to')} required />
        </div>
        <div>
          <label htmlFor="compose-subject">Subject</label>
          <input id="compose-subject" value={form.subject} onChange={set('subject')} required maxLength={200} />
        </div>
        <div>
          <label htmlFor="compose-body">Message</label>
          <textarea
            id="compose-body"
            rows={8}
            value={form.body}
            onChange={set('body')}
            required
            aria-describedby="compose-body-hint"
          />
          {/* The API appends "<artist>\n<studio>" (routes/inbox.js), so a
              hand-typed sign-off would appear twice. */}
          <p id="compose-body-hint" className="muted small" style={{ marginTop: 4 }}>
            No need to sign off — your name and studio name are added to the end of the email
            automatically.
          </p>
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </form>
    </Dialog>
  );
}

/**
 * @param {number} bytes
 * @returns {string} e.g. "1.2 MB".
 */
function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
